import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { Box, Button, createTheme, Grid, ThemeProvider } from '@mui/material';

import { ROUTES } from 'src/utils/constant';
import { getMissionReport } from 'src/api/apis';
import { TokenExpiredError } from 'src/api/error';
import PageLayout from 'src/components/Layout/PageLayout';
import SearchInput from 'src/components/Input/SearchInput';
import CirclePercentage from 'src/components/Progress/CirclePercentage';
import SubmissionsDialog from 'src/components/Dialog/Submissions';

export default function MissionReportDetail () {
  const { t } = useTranslation();
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [mission, setMission] = useState(null);
  const [search, setSearch] = useState('');
  const [approvalMode, setApprovalMode] = useState(false);
  const [statistic, setStatistic] = useState({
    submittedRate: 0,
    approvedRate: 0,
    approvedCount: 0,
  });
  const { id } = useParams<{ id: string; }>();

  const displayUsers = users.filter(user => user.uid.includes(search));
  const submittedUsers = users.filter(user => user.submissions.length > 0 && user.submissions[0].status === 'PENDING');

  const getReport = () => {
    getMissionReport(id)
      .then(res => {
        const data = res.data;
        if (data.status === '1') {
          setUsers(data.report);
          setMission(data.mission);
          setStatistic({
            submittedRate: data.submitted_rate,
            approvedRate: data.approved_rate,
            approvedCount: data.approved_count,
          });
        }
      })
      .catch(err => {
        if (err instanceof TokenExpiredError) {
          history.push(ROUTES.login);
        }
      });
  };

  useEffect(() => {
    getReport();
  }, [id, history]);

  const approveSuccess = () => {
    getReport();
  };

  const statisticItem = (text: string) => {
    return (
      <div style={{ marginBottom: '15px' }}>
        <div
          style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            marginRight: '13.5px',
            backgroundColor: '#006EC2',
            borderRadius: '2px',
          }}
        ></div>
        <span style={{ fontSize: '18px', color: '#006EC2' }}>{text}</span>
      </div>
    );
  };

  return (
    <PageLayout
      topRight={
        <div style={{ display: 'flex', gap: '20px' }}>
          <SearchInput onTextChange={text => { setSearch(text); }} />
          <Button
            sx={{
              width: '125px',
              height: '34px',
              textTransform: 'capitalize',
            }}
            variant="contained"
            startIcon={<i className="bi bi-arrow-return-left"></i>}
            onClick={() => { history.goBack(); }}
          >
            {t('Back')}
          </Button>
        </div>
      }
      title={mission ? mission.title.en : ''}
      isAdmin={true}
    >
      <Grid container spacing={2} mt={2} mb={8}>
        <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
          <Box sx={{ width: '200px' }}>
            <CirclePercentage current={statistic.approvedCount} total={users.length} />
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '27px' }}>
              {statisticItem(`${t('Submit rate')}: ${Math.round(statistic.submittedRate * 10000) / 100}%`)}
              {statisticItem(`${t('Approval rate')}: ${Math.round(statistic.approvedRate * 10000) / 100}%`)}
              <Button variant="contained" sx={{ marginTop: '10px' }} onClick={() => { setApprovalMode(true); }}>
                {t('Approve Submission')}
              </Button>
            </div>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={9} xl={9}>
          <ThemeProvider
            theme={() => {
              return createTheme({
                components: {
                  MuiTableCell: {
                    styleOverrides: {
                      root: {
                        fontSize: '16px',
                        lineHeight: '22px',
                      },
                      head: {
                        background: '#006EC2 !important',
                        color: '#fff !important',
                        fontSize: '16px',
                        '& .MUITableSortLabel-icon': { color: '#fff !important' },
                      },
                      footer: {
                        background: '#fff !important',
                      },
                      stickyHeader: {
                        color: '#fff !important',
                      },
                    },
                  },
                  MuiTableRow: {
                    styleOverrides: {
                      root: {
                        '&:nth-of-type(odd)': {
                          background: '#E5F0F9',
                        },
                        '&:nth-of-type(even)': {
                          background: '#F5F9FD',
                        },
                        // hide last border
                        '&:last-child td, &:last-child th': {
                          border: 0,
                        },
                      },
                      head: {
                        background: '#006EC2 !important',
                        color: '#fff !important',
                      },
                    },
                  },
                  MuiButton: {
                    styleOverrides: {
                      root: {
                        'div div': {
                          color: '#fff !important',
                        },
                      },
                    },
                  },
                },
              });
            }}
          >
            <MUIDataTable
              title={' '}
              data={displayUsers.map(user => {
                return {
                  uid: user.uid,
                  company: user.company[0].company_name,
                  department: user.department,
                  uploadedTime: user.submissions[0]?.timestamp
                    ? moment(user.submissions[0]?.timestamp).format('DD/MM/YYYY HH:mm')
                    : '-',
                  score: user.submissions[0]?.status === 'APPROVED' ? mission?.score : '-',
                  approvalTime: user.submissions[0]?.approved_at
                    ? moment(user.submissions[0]?.approved_at).format('DD/MM/YYYY HH:mm')
                    : '-',
                  approval: user.submissions[0]?.status === 'APPROVED' ? 'YES' : '-',
                };
              })}
              columns={[
                {
                  name: 'uid',
                  label: t('UID'),
                },
                {
                  name: 'company',
                  label: t('Company'),
                },
                {
                  name: 'department',
                  label: t('Department'),
                },
                {
                  name: 'uploadedTime',
                  label: t('Uploaded Time'),
                },
                {
                  name: 'score',
                  label: t('Score'),
                },
                {
                  name: 'approvalTime',
                  label: t('Approval Time'),
                },
                {
                  name: 'approval',
                  label: t('Approval'),
                },
              ]}
              options={{
                responsive: 'vertical',
                filterType: 'multiselect',
                fixedHeader: true,
                print: false,
                viewColumns: false,
                fixedSelectColumn: false,
                selectableRows: 'none',
                download: false,
                filter: false,
                search: false,
                pagination: false,
                textLabels: {
                  filter: {
                    all: t('All'),
                    title: t('FILTERS'),
                    reset: t('RESET'),
                  },
                  pagination: {
                    next: t('Next Page'),
                    previous: t('Previous Page'),
                    rowsPerPage: t('Rows per page') + ':',
                    displayRows: t('of'),
                  },
                },
              }}
              components={{
                TableToolbar: () => <></>,
              }}
            />
          </ThemeProvider>
        </Grid>
      </Grid>
      <SubmissionsDialog
        show={approvalMode}
        users={submittedUsers}
        mission={mission}
        onClose={() => { setApprovalMode(false); }}
        onSuccess={approveSuccess}
      />
    </PageLayout>
  );
}
